import { useState } from 'react';
import { Role } from '@pickem/shared';
import type { LeagueMemberDTO } from '@pickem/shared';
import { useApi } from '../../lib/useApi';
import { Spinner, ErrorBanner, Card, SectionTitle, InfoBanner } from '../../components/ui';
import { api, ApiError } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { formatDateTime } from '../../lib/format';

interface PendingInvite {
  id: string;
  email: string;
  invitedAt: string;
  expiresAt: string;
}

interface StaffData {
  members: LeagueMemberDTO[];
  pendingInvites: PendingInvite[];
}

export function AdminStaffPage() {
  const { activeLeagueId } = useAuth();

  if (!activeLeagueId) {
    return <InfoBanner>Select a league first.</InfoBanner>;
  }

  return <AdminStaffForLeague leagueId={activeLeagueId} />;
}

function AdminStaffForLeague({ leagueId }: { leagueId: string }) {
  const { data, error, loading, reload } = useApi<StaffData>(`/admin/leagues/${leagueId}/staff`);
  const [email, setEmail] = useState('');
  const [formErr, setFormErr] = useState('');
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState('');

  async function invite(e: React.FormEvent) {
    e.preventDefault();
    setFormErr('');
    setBusy(true);
    try {
      await api.post(`/admin/leagues/${leagueId}/staff/invite`, { email });
      setEmail('');
      reload();
    } catch (err) {
      setFormErr(err instanceof ApiError ? err.message : 'Unable to send invite.');
    } finally {
      setBusy(false);
    }
  }

  async function removeStaff(userId: string) {
    if (!confirm('Remove this staff member from the league?')) return;
    setBusy(true);
    setNotice('');
    try {
      const res = await api.del<{ warning: string | null }>(
        `/admin/leagues/${leagueId}/staff/${userId}`,
      );
      if (res.warning) setNotice(res.warning);
      reload();
    } catch (err) {
      setFormErr(err instanceof ApiError ? err.message : 'Unable to remove staff member.');
    } finally {
      setBusy(false);
    }
  }

  async function revokeInvite(inviteId: string) {
    setBusy(true);
    try {
      await api.del(`/admin/leagues/${leagueId}/invites/${inviteId}`);
      reload();
    } catch (err) {
      setFormErr(err instanceof ApiError ? err.message : 'Unable to revoke invite.');
    } finally {
      setBusy(false);
    }
  }

  if (loading) return <Spinner />;
  if (error) return <ErrorBanner message={error} />;

  const staff = (data?.members ?? []).filter((m) => m.role === Role.STAFF);

  return (
    <div className="space-y-4">
      <SectionTitle>Staff</SectionTitle>

      {notice && <InfoBanner>{notice}</InfoBanner>}
      {formErr && <ErrorBanner message={formErr} />}

      <Card>
        <h3 className="mb-3 font-display text-xl uppercase text-ink">Current staff</h3>
        <table className="w-full border-collapse text-sm">
          <thead>
            <tr className="border-b border-line text-left text-xs uppercase tracking-wide text-muted">
              <th className="px-2 py-2">Name</th>
              <th className="px-2 py-2">Email</th>
              <th className="px-2 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {staff.map((m) => (
              <tr key={m.userId} className="border-b border-line last:border-0">
                <td className="px-2 py-2 font-medium">{m.name}</td>
                <td className="px-2 py-2 text-muted">{m.email}</td>
                <td className="px-2 py-2 text-right">
                  <button
                    className="text-danger underline"
                    disabled={busy}
                    onClick={() => removeStaff(m.userId)}
                  >
                    Remove
                  </button>
                </td>
              </tr>
            ))}
            {staff.length === 0 && (
              <tr>
                <td colSpan={3} className="px-2 py-4 text-center text-muted">
                  No staff members yet.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </Card>

      <Card>
        <h3 className="mb-3 font-display text-xl uppercase text-ink">Pending invites</h3>
        <table className="w-full border-collapse text-sm">
          <thead>
            <tr className="border-b border-line text-left text-xs uppercase tracking-wide text-muted">
              <th className="px-2 py-2">Email</th>
              <th className="px-2 py-2">Invited</th>
              <th className="px-2 py-2">Expires</th>
              <th className="px-2 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {data?.pendingInvites.map((inv) => (
              <tr key={inv.id} className="border-b border-line last:border-0">
                <td className="px-2 py-2 font-medium">{inv.email}</td>
                <td className="px-2 py-2 text-muted">{formatDateTime(inv.invitedAt)}</td>
                <td className="px-2 py-2 text-muted">{formatDateTime(inv.expiresAt)}</td>
                <td className="px-2 py-2 text-right">
                  <button
                    className="text-danger underline"
                    disabled={busy}
                    onClick={() => revokeInvite(inv.id)}
                  >
                    Revoke
                  </button>
                </td>
              </tr>
            ))}
            {data?.pendingInvites.length === 0 && (
              <tr>
                <td colSpan={4} className="px-2 py-4 text-center text-muted">
                  No pending invites.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </Card>

      <Card>
        <h3 className="mb-3 font-display text-xl uppercase text-ink">Invite staff by email</h3>
        <form onSubmit={invite} className="flex flex-wrap items-end gap-3">
          <label className="text-xs">
            <span className="mb-1 block text-muted">Email</span>
            <input
              type="email"
              className="input w-64"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </label>
          <button type="submit" className="btn-primary" disabled={busy}>
            Send invite
          </button>
        </form>
      </Card>
    </div>
  );
}
